/**
 * 権限(docs/20 §3・§5): owner がメールで role を付与・剥奪し、監査ログを新しい順に見る。
 *
 * owner 以外にはこの画面を出さない(Console 側で出し分け)。付与・剥奪は `adminSetRole` callable。
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ADMIN_ROLES, ROLE_LABELS, type AdminRole } from '../services/adminAuth';
import {
  formatDateTime,
  newOperationId,
  toCmsError,
  type AuditEntry,
  type CmsCallables,
  type CmsError,
  type CmsReader,
} from '../services/cms';

interface Props {
  readonly cms: CmsCallables;
  readonly reader: CmsReader;
}

/** 剥奪を表す選択肢の値。 */
const REVOKE = 'revoke';

function short(value: unknown): string {
  if (value === null || value === undefined) return '—';
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  return text.length > 80 ? `${text.slice(0, 80)}…` : text;
}

export function RolesPanel({ cms, reader }: Props) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AdminRole | typeof REVOKE>('viewer');
  const [pending, setPending] = useState<{
    readonly email: string;
    readonly role: AdminRole | null;
    readonly operationId: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<CmsError | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [audit, setAudit] = useState<readonly AuditEntry[] | null>(null);
  const [auditError, setAuditError] = useState<string | null>(null);

  const reloadAudit = useCallback(async () => {
    setAuditError(null);
    try {
      setAudit(await reader.listAudit());
    } catch (cause) {
      setAuditError(String(cause));
    }
  }, [reader]);

  useEffect(() => {
    void reloadAudit();
  }, [reloadAudit]);

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    const trimmed = email.trim();
    if (trimmed === '') return;
    setError(null);
    setDone(null);
    setPending({
      email: trimmed,
      role: role === REVOKE ? null : role,
      operationId: newOperationId(),
    });
  }

  async function onConfirm(): Promise<void> {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      await cms.setRole(pending.email, pending.role, pending.operationId);
      setDone(
        pending.role === null
          ? `${pending.email} の権限を外しました。`
          : `${pending.email} を ${pending.role} にしました。`,
      );
      setPending(null);
      setEmail('');
      await reloadAudit();
    } catch (cause) {
      const failure = toCmsError(cause);
      setError(failure);
      if (failure.code !== 'network') setPending(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pane pane--page">
      <h2 className="section-title">権限</h2>
      <section className="card" aria-label="権限の付与・剥奪">
        <h3>権限の付与・剥奪</h3>
        <p className="muted">
          相手は先に管理画面のアカウント(メール/パスワード)を持っている必要があります。反映は相手の次回ログイン(またはトークン更新)からです。
        </p>
        <form className="toolbar" onSubmit={onSubmit}>
          <input
            className="input"
            type="email"
            placeholder="メールアドレス"
            aria-label="メールアドレス"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
          <select
            className="select select--inline"
            aria-label="権限"
            value={role}
            onChange={(event) => {
              setRole(event.target.value as AdminRole | typeof REVOKE);
            }}
          >
            {ADMIN_ROLES.map((value) => (
              <option key={value} value={value}>
                {ROLE_LABELS[value]}
              </option>
            ))}
            <option value={REVOKE}>権限を外す</option>
          </select>
          <button className="button button--primary" type="submit" disabled={busy}>
            反映する
          </button>
        </form>
      </section>

      {done !== null && (
        <div className="notice notice--ok" role="status">
          {done}
        </div>
      )}
      {error && (
        <div className="notice notice--error" role="alert">
          {error.message}
        </div>
      )}

      <section className="card" aria-label="監査ログ">
        <div className="toolbar">
          <h3>監査ログ(新しい順)</h3>
          <div className="topbar__spacer" />
          <button
            className="button button--small"
            type="button"
            onClick={() => {
              void reloadAudit();
            }}
          >
            再読み込み
          </button>
        </div>
        {auditError !== null && (
          <div className="notice notice--error" role="alert">
            監査ログを読み込めませんでした: {auditError}
          </div>
        )}
        {audit === null ? (
          <p className="muted">読み込み中…</p>
        ) : audit.length === 0 ? (
          <p className="muted">記録はまだありません。</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>日時</th>
                <th>操作者</th>
                <th>操作</th>
                <th>対象</th>
                <th>前</th>
                <th>後</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((entry) => (
                <tr key={entry.id}>
                  <td>{formatDateTime(entry.at || null)}</td>
                  <td className="mono">{entry.email ?? entry.uid}</td>
                  <td className="mono">{entry.action}</td>
                  <td className="mono">{entry.target}</td>
                  <td className="mono">{short(entry.before)}</td>
                  <td className="mono">{short(entry.after)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {pending && (
        <div className="dialog-backdrop" role="presentation">
          <div className="dialog" role="dialog" aria-modal="true" aria-label="権限の変更">
            <h2 className="dialog__title">権限を変更しますか?</h2>
            <p className="dialog__target mono">{pending.email}</p>
            <p>
              {pending.role === null
                ? '管理画面の権限を外します。'
                : `role を ${pending.role} にします。`}
            </p>
            <div className="dialog__actions">
              <button
                className="button"
                type="button"
                disabled={busy}
                onClick={() => {
                  setPending(null);
                }}
              >
                やめる
              </button>
              <button
                className="button button--primary"
                type="button"
                disabled={busy}
                onClick={() => {
                  void onConfirm();
                }}
              >
                変更する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
